import React from 'react';
import { useIntl } from 'umi';
import { Space, Tag, Typography } from 'antd';
import { MessageMediumRenderer, MessageRendererProps } from '@/components/Communication/types';
import { MailOutlined } from '@ant-design/icons';

import { F } from '@/utils/intl';
import PrettyDate from '@/components/PrettyDate';
import styles from './Email.less';

const { Text } = Typography;

const EmailSummary: React.FC<MessageRendererProps> = ({ message }) => (
  <div className={styles.emailSummaryContainer}>
    <MailOutlined className={styles.emailIcon} />
    <div className={styles.emailSummaryHeader}>
      <Text strong ellipsis className={styles.emailSummarySubject}>
        {message.subject || (
          <F id="common.no_subject" defaultMessage="(No Subject)" />
        )}
      </Text>
      <Text type="secondary" ellipsis className={styles.emailSummaryContent}>
        {message.content}
      </Text>
    </div>
  </div>
);

const EmailExtensive: React.FC<MessageRendererProps> = ({ message }) => (
  <div className={styles.emailExtensiveContainer}>
    <div className={styles.emailExtensiveHeader}>
      <Text strong ellipsis className={styles.emailExtensiveSubject}>
        <MailOutlined className={styles.emailIcon} /> {message.subject || <F id="common.no_subject" defaultMessage="(No Subject)" />}
      </Text>
      <Text type="secondary" className={styles.emailExtensiveTimestamp}>
        <PrettyDate date={message.timestamp} />
      </Text>
    </div>
    <div className={styles.emailExtensiveSender}>
      <F id="communication.attribute.from" defaultMessage="From:" /> {message.sender}
    </div>
    <Text ellipsis={{ rows: 1 }} className={styles.emailExtensiveContentSnippet}>{message.content}</Text>
  </div>
);

const EmailPreview: React.FC<MessageRendererProps> = ({ message }) => {
  const intl = useIntl();

  return (
    <div className={styles.emailPreviewContainer}>
      <Space direction="vertical" className={styles.emailPreviewSpace} size="middle">
        <div>
          <Tag color="orange">
            <MailOutlined /> <F id="communication.medium.email" defaultMessage="Email" />
          </Tag>
          <Typography.Title level={5} className={styles.emailPreviewTitle}>
            {message.subject || <F id="common.no_subject" defaultMessage="(No Subject)" />}
          </Typography.Title>
        </div>
        <div className={styles.emailPreviewInfo}>
          <Text strong><F id="communication.attribute.from" defaultMessage="From:" /> </Text><Text>{message.sender}</Text><br/>
          <Text strong><F id="communication.attribute.to" defaultMessage="To:" /> </Text><Text>{message.recipient}</Text><br/>
          <Text type="secondary" className={styles.emailPreviewTimestamp}>
            <PrettyDate date={message.timestamp} />
          </Text>
        </div>
        <div className={styles.emailPreviewContent}>
          {message.html ? (
            <iframe
              srcDoc={`
                <style>
                  a, button { pointer-events: none !important; cursor: default !important; }
                </style>
                ${message.html}
              `}
              title={intl.formatMessage({ id: 'communication.email.content', defaultMessage: 'Email Content' })}
              className={styles.emailIframe}
              frameBorder="0"
              width="100%"
              sandbox="allow-same-origin"
            />
          ) : (
            <div className={styles.plainTextContent}>{message.content}</div>
          )}
        </div>
      </Space>
    </div>
  );
};

const EmailRenderers: MessageMediumRenderer = {
  Summary: EmailSummary,
  Extensive: EmailExtensive,
  Preview: EmailPreview,
};

export default EmailRenderers;
